import React, { useEffect, useState } from 'react'
import { Col, Container, Form, Image, OverlayTrigger, Popover, Row, Tooltip, Tab, Nav, Button } from 'react-bootstrap'
import { Line } from 'react-chartjs-2'
import EllipsisWithTooltip from 'react-ellipsis-with-tooltip'
import { formatS2kData, getPreviousScore, getTotalS2kScore, formatMiddleName, getFlareIndex, transformDate } from '../../../utils/helpers'
import { useDispatch, useSelector } from 'react-redux'
import { setPga, updateS2k, updateFlare, updateLLDAS, updateDamage, setPendS2k, setNoFlare, resetFlare, resetLLDAS, setNoDamageIndex, resetDamage  } from '../../../redux/actions/assessment'
import { showSynopsis } from '../../../redux/actions/synopsis'
import InfoCircle from '../../../assets/icons/info-circle-fill.svg'
import Clipboard from '../../../assets/icons/clipboard.svg'
import Calendar from '../../../assets/icons/calendar2-event.svg'
import Wrench from '../../../assets/icons/wrench.svg'
import Book from '../../../assets/icons/book.svg'
import QuestionCirle from '../../../assets/icons/question-circle.svg'
import { format } from 'date-fns'
import useDimensions from '../../../hooks/useDimensions'
import usePrevious from '../../../hooks/usePrevious'

const findPreviousScore = (itemId, s2kScores) => {
  let previousScore = 0

  if (s2kScores.length > 0) {
    const prevS2kScore = s2kScores[s2kScores.length - 1]
    const prevScore = prevS2kScore.items.find((item) => item.itemId === itemId)
    previousScore = prevScore ? prevScore.item.points : 0
  }

  return previousScore
}

const renderPopover = (s2kItem) => {
  return (
    <Popover id='item-popover'>
      <Popover.Title as='h3'>Historical Data</Popover.Title>
      <Popover.Content>
        {
          s2kItem.trendedLabs.length > 0
            ? s2kItem.trendedLabs.sort((a, b) => {
              if (a.code < b.code) return -1
              if (a.code > b.code) return 1
              return 0
            }).map((item) => (
              <div key={item.patientObservationId}>
                {format(new Date(item && item.effectiveDate), 'M/dd/yyyy')}: {item.value} {item.valueUnit}
              </div>
            ))
            : 'No labs data found'
        }
      </Popover.Content>
    </Popover>
  )
}

const itemHasHistoricalData = (s2kItem) => {
  return s2kItem.showHistoricalData === 1 && s2kItem.trendedLabs.length > 0
}

const getCurrentDamageScore = (damage) => {
  return [].concat(...damage.map((d) => d.rows)).reduce((acc, curr) => {
    return acc + curr.score
  }, 0)
}

const Assessment = () => {
  const [key, setKey] = useState('flare')
  const [chartData, setChartData] = useState({})
  const { width } = useDimensions()

  const dispatch = useDispatch()
  const { 
    pga, 
    flare,
    flareIsNull,
    s2k, 
    patient, 
    s2kItems, 
    s2kScores, 
    content, 
    lldas, 
    damage, 
    damages,
    damageIsNull,
    pendS2k,
    assessmentIsSuccess,
    encounter
  } = useSelector((state) => ({
    pga: state.assessment.pga,
    flare: state.assessment.flare,
    flareIsNull: state.assessment.flareIsNull,
    s2k: state.assessment.s2k,
    s2kScores: state.assessment.s2kScores,
    patient: state.patient.patient,
    s2kItems: state.assessment.s2kItems,
    content: state.healthSystem.content,
    lldas: state.assessment.lldas,
    damage: state.assessment.damage,
    damages: state.assessment.damages,
    damageIsNull: state.assessment.damageIsNull,
    pendS2k: state.assessment.pendS2k,
    assessmentIsSuccess: state.assessment.isSuccess,
    encounter: state.patient.encounter
  }))

  const s2kInfo = content.find((c) => c.contentTypeId === 2)
  const flareInfo = content.find((c) => c.contentTypeId === 3)
  const pgaInfo = content.find((c) => c.contentTypeId === 4)
  const lldasInfo = content.find((c) => c.contentTypeId === 5)
  const damageInfo = content.find((c) => c.contentTypeId === 6)

  const s2kScore = getTotalS2kScore(s2k)
  const previousS2kScore = usePrevious(s2kScore)

  const previousPendS2k = usePrevious(pendS2k)
  useEffect(() => {
    if (assessmentIsSuccess) {
      if (s2kScore !== previousS2kScore || pendS2k !== previousPendS2k) {
        const formattedData = formatS2kData(s2k, s2kScores, encounter.date, pendS2k)
        let labels = []
        let newData = []
  
        for (let i = 0; i < formattedData[0].data.length; i += 1) {
          const dataPoint = formattedData[0].data[i]
          labels.push(dataPoint.x)
          newData.push(dataPoint.y)
        }
  
        setChartData({
          labels,
          datasets: [{
            label: 'Sledai S2K',
            data: newData,
            backgroundColor: 'rgba(77, 137, 67, 1)',
            borderColor: 'rgba(77, 137, 67, 1)',
            borderWidth: 1,
            fill: false,
            pointBorderWidth: 2,
            lineTension: 0
          }]
        })
      }
    }
  }, [assessmentIsSuccess, s2kScore, pendS2k])

  const options = {
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        position: 'right',
        ticks: {
          beginAtZero: true,
          callback: (value) => Math.floor(value) === value ? value : ''
        }
      }],
      xAxes: [{
        gridLines: {
          display: false
        }
      }]
    },
    legend: {
      display: false
    },
    tooltips: {
      enabled: false,
      custom: function(tooltipModel) {
        // Tooltip Element
        let tooltipEl = document.getElementById('chartjs-tooltip')

        // Create element on first render
        if (!tooltipEl) {
          tooltipEl = document.createElement('div')
          tooltipEl.id = 'chartjs-tooltip'
          tooltipEl.innerHTML = '<table></table>'
          document.body.appendChild(tooltipEl)
        }

        // Hide if no tooltip
        if (tooltipModel.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        // Set caret Position
        tooltipEl.classList.remove('above', 'below', 'no-transform')
        if (tooltipModel.yAlign) {
          tooltipEl.classList.add(tooltipModel.yAlign)
        } else {
          tooltipEl.classList.add('no-transform')
        }

        const getBody = (title) => {
          if (title === format(new Date(transformDate(encounter.date)), 'M/dd/yyyy')) {
            return s2k.reduce((acc, curr) => {
              if (curr.checked) {
                const item = s2kItems.find((s2kItem) => s2kItem.itemId === curr.id)

                return [...acc, {
                  ...curr,
                  name: item.name
                }]
              }
              return acc     
            }, [])
          }

          const foundEncounter = s2kScores.find((s2kScore) => title === format(new Date(transformDate(s2kScore.encounterDate)), 'M/dd/yyyy'))
          return foundEncounter.items.map((item) => {
            const s2kItem = s2kItems.find((i) => i.itemId === item.itemId)
            return {
              ...item,
              name: s2kItem.name
            }
          })
        }

        // Set Text
        if (tooltipModel.body) {
          const titleLines = tooltipModel.title || []
          const bodyLines = getBody(tooltipModel.title[0])

          let innerHtml = '<thead>'

          titleLines.forEach(function(title) {
            innerHtml += '<tr><th>' + title + '</th></tr>'
          })

          innerHtml += '</thead><tbody>'

          bodyLines.forEach((body) => {
            innerHtml += '<tr style=\'padding: 0px; margin: 0px;\'><td>' + body.name + '</td></tr>'
          })
          innerHtml += '</tbody>'

          const tableRoot = tooltipEl.querySelector('table')
          tableRoot.innerHTML = innerHtml
        }

        // `this` will be the overall tooltip
        const position = this._chart.canvas.getBoundingClientRect()

        let leftPos = position.left + tooltipModel.caretX

        if (tooltipModel.caretX > 150) {
          leftPos =  leftPos - 138
        }
        // Display, position, and set styles for font
        tooltipEl.style.opacity = 1
        tooltipEl.style.position = 'absolute'
        tooltipEl.style.left = leftPos + 'px'
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px'
        tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily
        tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px'
        tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle
        tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px'
        tooltipEl.style.pointerEvents = 'none'
        tooltipEl.style.background = '#EEE'
        tooltipEl.style.borderRadius = '5px'
      }
    }
    // events: []
  }

  const handlePendS2k = () => {
    dispatch(setPendS2k(!pendS2k))
  }

  const breakpointHelper = (screenWidth, breakpointA, breakpointB, pendS2k = false) => {
    let breakpoint =  width <= screenWidth ? breakpointA : breakpointB

    if (pendS2k) {
      breakpoint = breakpoint - 1
    }

    return breakpoint
  }

  return (
    <div>
      <h2>Step 1: Assessment</h2>
      <Container fluid className='border'>
        <Row>
          <Col lg={8}>
            <Container id='s2k' fluid className='shadow-sm my-2 pb-2'>
              <Form id='s2k'>
                <Form.Row className='border-bottom align-items-center'>
                  <Col xs={breakpointHelper(1250, 4, 2)}>
                    <h3 className='h5'>SLEDAI-2K
                      <OverlayTrigger
                        placement='right'
                        overlay={
                          <Tooltip id='s2k-tooltip'>
                            {s2kInfo ? s2kInfo.description : ''}
                          </Tooltip>
                        }
                      >
                        <Image src={InfoCircle} alt='More Info' className='ml-1 filter-s2k' />
                      </OverlayTrigger>

                      {
                        width <= 1200 && (
                          <OverlayTrigger
                            placement='right'
                            overlay={
                              <Tooltip id='s2k-tooltip'>
                              Check all that apply. Chart indicated items are pre-selected. Previous score is shown to the left of today&apos;s.
                              </Tooltip>
                            }
                          >
                            <Image src={QuestionCirle} alt='S2K Info' className='ml-2'/>
                          </OverlayTrigger>
                        )
                      }
                    </h3>
                  </Col>
                  <Col xs={breakpointHelper(1250, 7, 9, pendS2k)}>
                    {
                      width > 1250 && (
                        <p className='mt-2'>Check all that apply. Chart indicated items are pre-selected. Previous score is shown to the left of today&apos;s.</p>
                      ) 
                    }
                    {
                      width > 1200 && width < 1250 && (
                        <div className='mt-2 text-center'>
                          <p className='my-0'>Check all that apply. Chart indicated items are pre-selected.</p>
                          <p className='my-0 mb-1'>Previous score is shown to the left of today&apos;s.</p>
                        </div>
                      )
                    }
                  </Col>
                  <Col xs={pendS2k ? 2 : 1}>
                    <span
                      className='mr-2'
                    >
                      {getPreviousScore(s2kScores).value}
                    </span>
                    {
                      pendS2k ? (
                        <span className='font-weight-bold'>
                          PENDING
                        </span>
                      ) : (
                        <span className='h5 font-weight-bold'>
                          {getTotalS2kScore(s2k)}
                        </span>
                      )
                    }
                    
                  </Col>
                </Form.Row>
                <Form.Row className='border-bottom py-1 bg-light'>
                  <Col xs={12}>
                    <Button 
                      size='sm' 
                      className='float-right'
                      onClick={handlePendS2k}
                    >
                      {pendS2k ? 'Re-Activate SLEDAI-2k' : 'Pend SLEDAI-2k'}
                    </Button>
                  </Col>
                </Form.Row>
                <Form.Row className='border-bottom py-1'>
                  <Col xs={width > 1230 ? 3 : 4} />
                  <Col xs={width > 1230 ? 2 : 3}>
                    {
                      width >= 1440 ? (
                        <>
                          <Image src={Clipboard} alt='Patient Results' /> Patient Results
                        </>
                      ) : (
                        <OverlayTrigger
                          placement='bottom'
                          overlay={
                            <Tooltip id='s2k-tooltip'>
                              Patient Results
                            </Tooltip>
                          }
                        >
                          <Image src={Clipboard} alt='Patient Results' /> 
                        </OverlayTrigger>
                      )
                    }
                    
                  </Col>
                  <Col xs={2}>
                    {
                      width >= 1440 ? (
                        <>
                          <Image src={Calendar} alt='Calendar' /> Date
                        </>
                      ) : (
                        <OverlayTrigger
                          placement='bottom'
                          overlay={
                            <Tooltip id='s2k-tooltip'>
                              Date
                            </Tooltip>
                          }
                        >
                          <Image src={Calendar} alt='Calendar' />
                        </OverlayTrigger>
                      )
                    }
                  </Col>
                  <Col xs={1}>
                    <OverlayTrigger
                      placement='bottom'
                      overlay={
                        <Tooltip id='s2k-tooltip'>
                          Historical Data
                        </Tooltip>
                      }
                    >
                      <Image src={Wrench} alt='Historical Data' />
                    </OverlayTrigger>
                    
                  </Col>
                  <Col xs={width > 1230 ? 3 : 1} className={width < 1440 ? 'text-center' : ''}>
                    {
                      width >= 1440 ? (
                        <>
                          <Image src={Book} alt='Clinical Guidelines' /> Clinical Guidelines
                        </>
                      ) : (
                        <OverlayTrigger
                          placement='bottom'
                          overlay={
                            <Tooltip id='s2k-tooltip'>
                              Clinical Guidelines
                            </Tooltip>
                          }
                        >
                          <Image src={Book} alt='Clinical Guidelines' />
                        </OverlayTrigger>
                      )
                    }
                  </Col>
                  <Col xs='auto' className='text-muted' />
                  <Col xs='auto' />
                </Form.Row>
                {
                  s2kItems.map((s2kItem) => {
                    const foundS2kItem = s2k.find((s) => s.id === s2kItem.itemId)
                    return (
                      <Form.Row key={s2kItem.itemId} className={`border-bottom${foundS2kItem && foundS2kItem.checked ? ' bg-light' : ''}`}>
                        <Col xs={width > 1230 ? 3 : 4}>
                          <div className='custom-control custom-checkbox'>
                            <input
                              type='checkbox'
                              className='custom-control-input'
                              checked={foundS2kItem && foundS2kItem.checked}
                              onChange={(event) => dispatch(updateS2k(event))}
                              name={s2kItem.itemId}
                              id={s2kItem.itemId}
                            />
                            <label className='custom-control-label' htmlFor={s2kItem.itemId}>{s2kItem.name} ({s2kItem.points})</label>
                          </div>
                        </Col>
                        <Col xs={width > 1230 ? 2 : 3}>
                          {
                            foundS2kItem.results && foundS2kItem.results.map((result, index) => (
                              <div key={`result-${index}`}>
                                {
                                  result.value
                                    ? `${result.value} ${result.valueUnit ? result.valueUnit : ''}`
                                    : result.code
                                }
                              </div>
                            ))
                          }
                        </Col>
                        <Col xs={2}>
                          {
                            foundS2kItem.dates && foundS2kItem.dates.map((date, index) => (
                              <div key={`result-${date}-${index}`}>
                                {date}
                              </div>
                            ))
                          }
                        </Col>
                        <Col xs={1}>
                          {
                            (itemHasHistoricalData(foundS2kItem)) && (
                              <OverlayTrigger
                                placement='top'
                                overlay={renderPopover(foundS2kItem)}
                              >
                                <Image src={Wrench} alt='Historical Data' />
                              </OverlayTrigger>
                            )
                          }
                        </Col>
                        <Col xs={width > 1230 ? 3 : 1} className={`text-truncate${width <= 1230 ? ' text-center' : ''}`}>
                          {
                            width > 1230 ? (
                              <EllipsisWithTooltip placement={foundS2kItem.points === 8 ? 'top' : 'bottom'}>
                                {s2kItem.guideline}
                              </EllipsisWithTooltip>
                            ) : (
                              <OverlayTrigger
                                placement={foundS2kItem.points === 8 ? 'top' : 'bottom'}
                                overlay={
                                  <Tooltip id='s2k-tooltip'>
                                    {s2kItem.guideline} 
                                  </Tooltip>
                                }
                              >
                                <Image src={Book} alt='Clinical Guidelines' />
                              </OverlayTrigger>
                            )
                          }
                          
                        </Col>
                        <Col xs='auto' className='text-muted'>
                          {findPreviousScore(s2kItem.itemId, s2kScores)}
                        </Col>
                        <Col xs='auto' className='text-right'>
                          {foundS2kItem && foundS2kItem.score}
                        </Col>
                      </Form.Row>
                    )
                  })
                }
              </Form>
            </Container>
          </Col>
          <Col lg={4}>
            <Container id='pp' fluid className='shadow-sm my-2 py-2'>
              <h3 className='h5'>
                {patient && `${patient.firstName} ${formatMiddleName(patient.middleName)} ${patient.lastName}`}
              </h3>
              <p className='font-weight-bold'>
                {patient && `MRN ${patient.mrn}`}
              </p>
              <Row>
                <Col>
                  <h3 className='h6 green'>
                    SLEDAI-2K
                  </h3>
                </Col>
                <Col className='text-right'>
                  <Button size='sm' variant='link' className='font-75' onClick={() => dispatch(showSynopsis(true))}>Full Synopsis</Button>
                </Col>
              </Row>
              <div className='border' style={{ height: '185px', position: 'relative' }}>
                <Line options={options} data={chartData} />
              </div>
            </Container>
            <Container id='pga' fluid className='shadow-sm my-2 pb-2'>
              <Form id='pga'>
                <Form.Row className='align-items-baseline'>
                  <Col xs={10}>
                    <h3 className={width <= 1250 ? 'h6' : 'h5'}>
                      Physician&apos;s Global Assessment
                      <OverlayTrigger
                        placement='left'
                        overlay={
                          <Tooltip id='pga-tooltip'>
                            {pgaInfo ? pgaInfo.description : ''}
                          </Tooltip>
                        }
                      >
                        <Image src={InfoCircle} alt='More Info' className='ml-1 filter-purple' />
                      </OverlayTrigger>
                    </h3>
                  </Col>
                  <Col xs={2}>
                    {pga >= 0 && <p className='h5 font-weight-bold'>{parseFloat(pga).toFixed(2)}</p>}
                  </Col>
                </Form.Row>
                <Form.Row>
                  <Col>
                    <input
                      type='range'
                      className='form-control-range'
                      id='pga'
                      name='pga'
                      min='-1.00'
                      max='3.00'
                      step='0.25'
                      value={pga}
                      onChange={(event) => dispatch(setPga(event.target.value))}
                      onMouseUp={(event) => {
                        const score = event.target.value
                        if (score < 0 && score >= -0.5) dispatch(setPga(0))

                        if (score < 0 && score < -0.5) dispatch(setPga(-1))
                      }}
                      list='ticks'
                    />
                    <datalist id='ticks'>
                      <option>No Score</option>
                      <option>0</option>
                      <option>1</option>
                      <option>2</option>
                      <option>{width <= 1106 && width >= 996 ? '' : 'Severe'} 3</option>
                    </datalist>
                  </Col>
                </Form.Row>
                <Form.Row className='mt-2'>
                  <Col>
                    <Button 
                      variant='outline-secondary' 
                      size='sm'
                      disabled={pga < 0}
                      onClick={() => dispatch(setPga(-1))}
                    >
                      Reset
                    </Button>
                  </Col>
                </Form.Row>
              </Form>
            </Container>
            <Container id='fi' fluid className='shadow-sm my-2 pb-2'>
              <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
                <Nav variant='tabs' fill style={{ marginLeft: -15, marginRight: -15 }}>
                  <Nav.Item className='assessment-tab'>
                    <Nav.Link eventKey='flare'>Flare Index</Nav.Link>
                  </Nav.Item>
                  <Nav.Item className='assessment-tab'>
                    <Nav.Link eventKey='damage'>Damage Index</Nav.Link>
                  </Nav.Item>
                  <Nav.Item className='assessment-tab'>
                    <Nav.Link eventKey='lldas'>LLDAS</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey='damage'>
                    <Form id='di'>
                      <Form.Row className='border-bottom align-items-baseline'>
                        <Col xs={6}>
                          <h3 className='h5'>
                            Damage Index
                            <OverlayTrigger
                              placement='left'
                              overlay={
                                <Tooltip id='damage-tooltip'>
                                  {damageInfo ? damageInfo.description : ''}
                                </Tooltip>
                              }
                            >
                              <Image src={InfoCircle} alt='More Info' className='ml-1 filter-purple' />
                            </OverlayTrigger>
                          </h3>
                        </Col>
                        <Col xs={4}>
                          <span id='damage-check-text'>Check all that apply</span>
                        </Col>
                        <Col xs='auto' style={{ fontSize: damages[0] ? undefined : '10px' }}>
                          <span style={{ marginLeft: -5 }}>{damages[0] ? damages[0].score : 'N/A'}</span>
                        </Col>
                        <Col xs='auto' style={{ fontSize: damageIsNull ? '10px' : undefined }}>
                          <span style={{ marginLeft: -5 }}>{damageIsNull ? 'N/A' : getCurrentDamageScore(damage)}</span>
                        </Col>
                      </Form.Row>
                      <div style={{ maxHeight: '49vh', overflowY: 'auto', overflowX: 'hidden' }}>
                        {
                          damage.map((d) => (
                            <div key={d.section}>
                              <Form.Row className='border-bottom'>
                                <Col xs={12} id='damage-header-text' className={`${!d.section ? 'mb-3' : ''}`}>
                                  {d.section}
                                </Col>
                              </Form.Row>
                              {
                                d.rows.map((row) => {
                                  const previousDamageScore = damages[0] ? damages[0].items.find((item) => item.itemId === row.id) : null
                                  return (
                                    <Form.Row key={row.id} className='border-bottom'>
                                      <Col xs={10}>
                                        <div className='custom-control custom-checkbox'>
                                          <input
                                            type='checkbox'
                                            className='custom-control-input'
                                            name={row.id}
                                            id={row.id}
                                            checked={row.checked}
                                            onChange={(event) => dispatch(updateDamage(d.section, event))}
                                          />
                                          <label className='custom-control-label' htmlFor={row.id}>{row.name} ({row.points})</label>
                                        </div>
                                      </Col>
                                      <Col xs='auto'>
                                        {previousDamageScore ? previousDamageScore.result : 0}
                                      </Col>
                                      <Col xs='auto'>
                                        {row.score}
                                      </Col>
                                    </Form.Row>
                                  )
                                })
                              }
                            </div>

                          ))
                        }
                      </div>
                      <Form.Row className='mt-2'>
                        <Col xs={6}>
                          <Button 
                            size='sm' 
                            variant='outline-secondary'
                            onClick={() => dispatch(setNoDamageIndex())}
                          >
                            Damage Index = 0
                          </Button>
                        </Col>
                        <Col xs={2}>
                          <Button 
                            size='sm' 
                            variant='outline-secondary'
                            disabled={damageIsNull}
                            onClick={() => dispatch(resetDamage())}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Form.Row>
                    </Form>
                  </Tab.Pane>
                  <Tab.Pane eventKey='lldas'>
                    <Form id='fi'>
                      <Form.Row className='border-bottom align-items-baseline'>
                        <Col xs={12}>
                          <h3 className='h5'>
                            Lupus Low Disease Activity State
                            <OverlayTrigger
                              placement='left'
                              overlay={
                                <Tooltip id='lldas-tooltip'>
                                  {lldasInfo ? lldasInfo.description : ''}
                                </Tooltip>
                              }
                            >
                              <Image src={InfoCircle} alt='More Info' className='ml-1 filter-purple' />
                            </OverlayTrigger>
                          </h3>
                        </Col>
                      </Form.Row>
                      {
                        lldas.map((item) => (
                          <Form.Row key={item.id} className='border-bottom p-1'>
                            <Col xs={10}>
                              <span>
                                {item.name}
                              </span>
                            </Col>
                            <Col xs={2}>
                              <div className='custom-control custom-checkbox'>
                                <input
                                  type='checkbox'
                                  className='custom-control-input'
                                  name={item.id}
                                  id={item.id}
                                  checked={item.checked}
                                  onChange={(event) => dispatch(updateLLDAS(event))}
                                />
                                <label className='custom-control-label' htmlFor={item.id} />
                              </div>
                            </Col>
                          </Form.Row>
                        ))
                      }
                      <Form.Row className='mt-2'>
                        <Col>
                          <Button
                            variant='outline-secondary'
                            size='sm'
                            disabled={lldas.every((item) => !item.checked)}
                            onClick={() => dispatch(resetLLDAS())}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Form.Row>
                    </Form>
                  </Tab.Pane>
                  <Tab.Pane eventKey='flare'>
                    <Form id='fi'>
                      <Form.Row className='border-bottom align-items-baseline'>
                        <Col xs={5}>
                          <h3 className='h5'>
                            Flare Index
                            <OverlayTrigger
                              placement='left'
                              overlay={
                                <Tooltip id='flare-tooltip'>
                                  {flareInfo ? flareInfo.description : ''}
                                </Tooltip>
                              }
                            >
                              <Image src={InfoCircle} alt='More Info' className='ml-1 filter-purple' />
                            </OverlayTrigger>
                          </h3>
                        </Col>
                        <Col xs={7} className='pb-1 h5 font-weight-bold text-right'>
                          {!flareIsNull && getFlareIndex(flare)}
                        </Col>
                      </Form.Row>
                      <Form.Row className='border-bottom pb-1'>
                        <Col xs={4} />
                        <Col xs={4}>
                          Mild/Moderate
                        </Col>
                        <Col xs={4}>
                          Severe
                        </Col>
                      </Form.Row>
                      {
                        flare.map((flareItem) => (
                          <Form.Row className='border-bottom' key={flareItem.name}>
                            <Col xs={4}>
                              {flareItem.name}
                            </Col>
                            <Col xs={4}>
                              <EllipsisWithTooltip placement='bottom'>
                                <div className='custom-control custom-checkbox text-truncate'>
                                  <input
                                    type='checkbox'
                                    className='custom-control-input'
                                    checked={flareItem.value === 'Mild/Moderate'}
                                    onChange={(event) => dispatch(updateFlare(flareItem.id, event.target.checked ? 'Mild/Moderate' : '', event.target.checked ? flareItem.mild.guideline : ''))}
                                    id={flareItem.mild.guideline}
                                  />
                                  <label className='custom-control-label' htmlFor={flareItem.mild.guideline}>
                                    {flareItem.mild.guideline}
                                  </label>
                                </div>
                              </EllipsisWithTooltip>
                            </Col>
                            <Col xs={4}>
                              <EllipsisWithTooltip placement='bottom'>
                                <div className='custom-control custom-checkbox text-truncate'>
                                  <input
                                    type='checkbox'
                                    className='custom-control-input'
                                    checked={flareItem.value === 'Severe'}
                                    onChange={(event) => dispatch(updateFlare(flareItem.id, event.target.checked ? 'Severe' : '', event.target.checked ? flareItem.severe.guideline : ''))}
                                    id={flareItem.severe.guideline}
                                  />
                                  <label className='custom-control-label' htmlFor={flareItem.severe.guideline}>
                                    {flareItem.severe.guideline}
                                  </label>
                                </div>
                              </EllipsisWithTooltip>
                            </Col>
                          </Form.Row>
                        ))
                      }
                      <Form.Row className='mt-2'>
                        <Col xs={3}>
                          <Button 
                            size='sm' 
                            variant='outline-secondary'
                            onClick={() => dispatch(setNoFlare())}
                          >
                            No Flare
                          </Button>
                        </Col>
                        <Col xs={2}>
                          <Button 
                            size='sm' 
                            variant='outline-secondary'
                            disabled={flareIsNull}
                            onClick={() => dispatch(resetFlare())}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Form.Row>
                    </Form>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Assessment
